import {
  Eye,
  EyeOff,
  InfoIcon,
  Lock,
  MessageSquareWarning,
  PencilRuler,
} from 'lucide-react';
import { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const postStatusBadgeVariants = cva('p-2', {
  variants: {
    visibility: {
      private: 'bg-orange-500',
      public: 'bg-green-500',
      unlisted: 'bg-black',
      draft: 'bg-yellow-500',
      rejected_with_comments: 'bg-red-500',
    },
  },
  defaultVariants: {
    visibility: 'public',
  },
});

interface PostStatusBadgeProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof postStatusBadgeVariants> {
  visibility:
    | 'private'
    | 'public'
    | 'unlisted'
    | 'draft'
    | 'rejected_with_comments';
}

function PostStatusIcon({
  visibility,
  size,
}: {
  visibility:
    | 'private'
    | 'public'
    | 'unlisted'
    | 'draft'
    | 'rejected_with_comments';
  size: string;
}) {
  switch (visibility) {
    case 'public':
      return <Eye size={size} />;
    case 'private':
      return <Lock size={size} />;
    case 'draft':
      return <PencilRuler size={size} />;
    case 'unlisted':
      return <EyeOff size={size} />;
    case 'rejected_with_comments':
      return <MessageSquareWarning size={size} />;
    default:
      return <InfoIcon size={size} />;
  }
}

function getPostStatusName(visibility: string) {
  switch (visibility) {
    case 'public':
      return 'Public';
    case 'private':
      return 'Pending for Review';
    case 'unlisted':
      return 'Unlisted';
    case 'draft':
      return 'Draft';
    case 'rejected_with_comments':
      return 'Required Edits';
    default:
      return 'Unknown';
  }
}

function PostStatusBadge({
  className,
  visibility,
  ...props
}: PostStatusBadgeProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge
            className={cn(
              postStatusBadgeVariants({ visibility }),
              'flex-shrink-0',
              className
            )}
            {...props}
          >
            <PostStatusIcon visibility={visibility} size="16" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <span className="capitalize text-md">
            {getPostStatusName(visibility)}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default PostStatusBadge;
